// Enrollment Card Component
// Date: January 21, 2026

import { Card, CardContent, CardFooter, CardHeader } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { Calendar, Clock, BookOpen, CheckCircle } from "lucide-react";
import { PracticeProjectEnrollment } from "../../types/practiceProjects";
import {
  getEnrollmentStatusColor,
  formatEnrollmentStatus,
  getDifficultyColor,
  formatDifficulty,
  formatRelativeTime,
} from "../../utils/practiceProjects.utils";
import paths from "../../routes";
import { Tooltip, TooltipTrigger, TooltipContent } from "../ui/tooltip";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { useAuth } from "../../App";
import { ROLES } from "../../constant/roles";

interface EnrollmentCardProps {
  enrollment: PracticeProjectEnrollment;
  onContinue?: (projectId: string) => void;
  showProjectDetails?: boolean;
}

export function EnrollmentCard({
  enrollment,
  onContinue,
  showProjectDetails = true,
}: EnrollmentCardProps) {
  const navigate = useNavigate();
  const project = enrollment.practiceProject;

  if (!project) {
    return null;
  }

  const isCompleted = enrollment.status === "completed";
  const progress = enrollment.completion_percentage;

  const { user } = useAuth();
  const isAdmin =
    user?.role === ROLES.ADMIN ||
    user?.role === ROLES.INSTRUCTOR ||
    user?.role === ROLES.TA;

  // Title display: trim long titles and show full title on hover
  const MAX_TITLE_LENGTH = 30;
  const displayTitle = project.title
    ? project.title.length > MAX_TITLE_LENGTH
      ? project.title.slice(0, MAX_TITLE_LENGTH) + "…"
      : project.title
    : "";

  const handleCardClick = () => {
    if (!isAdmin && project.status !== "published") {
      toast.error("The project is yet to be published, Contact your Admin");
      return;
    }
    navigate(paths.toPracticeProjectDetails(project.id));
  };

  const handleContinue = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAdmin && project.status !== "published") {
      toast.error("The project is yet to be published, Contact your Admin");
      return;
    }
    if (onContinue) {
      onContinue(project.id);
    } else {
      navigate(paths.toPracticeProjectDetails(project.id));
    }
  };

  return (
    <Card
      className="hover:shadow-lg transition-shadow cursor-pointer"
      onClick={handleCardClick}
    >
      <CardHeader className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <h3
                  className="font-semibold text-lg break-words whitespace-normal"
                  aria-label={project.title}
                  style={{ overflowWrap: "anywhere", wordBreak: "normal" }}
                >
                  {displayTitle}
                </h3>
              </TooltipTrigger>
              {project.title && project.title.length > MAX_TITLE_LENGTH && (
                <TooltipContent
                  className="max-w-xs break-words"
                  style={{ overflowWrap: "anywhere", wordBreak: "normal" }}
                >
                  <div className="text-sm text-foreground">{project.title}</div>
                </TooltipContent>
              )}
            </Tooltip>
          </div>
          <Badge className={getEnrollmentStatusColor(enrollment.status)}>
            {formatEnrollmentStatus(enrollment.status)}
          </Badge>
        </div>

        {showProjectDetails && (
          <div className="flex gap-2">
            <Badge className={getDifficultyColor(project.difficulty_level)}>
              {formatDifficulty(project.difficulty_level)}
            </Badge>
            {project.course && (
              <Badge variant="outline" className="text-xs">
                {project.course.context_label}
              </Badge>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Enrollment Metadata */}
        <div className="grid grid-cols-2 gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <div>
              <p className="text-xs">Started</p>
              <p className="font-medium text-foreground">
                {formatRelativeTime(enrollment.started_at)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <div>
              <p className="text-xs">Last Accessed</p>
              <p className="font-medium text-foreground">
                {formatRelativeTime(enrollment.last_accessed_at)}
              </p>
            </div>
          </div>
        </div>

        {/* Completion Badge */}
        {isCompleted && enrollment.completed_at && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-900">
                Completed on{" "}
                {format(new Date(enrollment.completed_at), "MMM dd, yyyy")}
              </p>
            </div>
          </div>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-1 min-h-12">
          {project.tags && project.tags.length > 0 ? (
            <>
              {project.tags.slice(0, 3).map((tag, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="text-xs max-h-7"
                >
                  {tag}
                </Badge>
              ))}
              {project.tags.length > 3 && (
                <Badge variant="secondary" className="text-xs max-h-7">
                  +{project.tags.length - 3}
                </Badge>
              )}
            </>
          ) : (
            <div className="h-12" aria-hidden="true" />
          )}
        </div>
      </CardContent>

      <CardFooter>
        <Button
          className="w-full"
          onClick={handleContinue}
          variant={isCompleted ? "outline" : "default"}
        >
          <BookOpen className="w-4 h-4 mr-2" />
          {isCompleted ? "Review Project" : "Continue Learning"}
        </Button>
      </CardFooter>
    </Card>
  );
}
